import { clsx } from 'clsx'
import { type ComponentPropsWithoutRef } from 'react'
import { CheckIcon } from '../../icons/CheckIcon'
import { MinusIcon } from '../../icons/MinusIcon'
import { tokens } from '../../theme/tokens.css'
import { Text } from '../Text/Text'
import { table, row, header, activeHeader, cell, activeCell, aligner, coveredText } from './ComparisonTable.css'
import { TitleDataCell } from './ComparisonTable.TitleDataCell'

function Root({ className, ...props }: ComponentPropsWithoutRef<'table'>) {
  return <table className={clsx(table, className)} {...props} />
}

function Head(props: ComponentPropsWithoutRef<'thead'>) {
  return <thead {...props} />
}

function Body(props: ComponentPropsWithoutRef<'tbody'>) {
  return <tbody {...props} />
}

function Row({ className, ...props }: ComponentPropsWithoutRef<'tr'>) {
  return <tr className={clsx(row, className)} {...props} />
}

type HeaderProps = ComponentPropsWithoutRef<'th'> & { active?: boolean; tableTitle?: boolean }

function Header({ children, active, tableTitle, ...props }: HeaderProps) {
  return (
    <th className={clsx(header, active && activeHeader)} {...props}>
      {children && (
        <Text as="span" size={tableTitle ? 'lg' : 'sm'} color={tableTitle ? 'textPrimary' : 'textTranslucentSecondary'}>
          {children}
        </Text>
      )}
    </th>
  )
}

type DataCellProps = ComponentPropsWithoutRef<'td'> & {
  alignment?: 'left' | 'center' | 'right'
  active?: boolean
}

function DataCell({ className, children, active, alignment = 'center', ...props }: DataCellProps) {
  return (
    <td className={clsx(cell, active && activeCell, className)} {...props}>
      <Text as="span" className={clsx(coveredText, aligner[alignment])} size={{ _: 'xs', lg: 'md' }}>
        {children}
      </Text>
    </td>
  )
}

function CoveredIcon(props: ComponentPropsWithoutRef<typeof CheckIcon>) {
  return <CheckIcon size="1.125rem" {...props} />
}

function NotCoveredIcon(props: ComponentPropsWithoutRef<typeof MinusIcon>) {
  return <MinusIcon size="1.125rem" color={tokens.legacyColors.gray500} {...props} />
}

export const ComparisonTable = {
  Root,
  Head,
  Body,
  Row,
  Header,
  TitleDataCell,
  DataCell,
  CoveredIcon,
  NotCoveredIcon,
}
